import { getContractStatusListItem } from 'components/ContractStatus';
import { ContractStatusEnum } from 'gql/graphql';
import type {
    BimContractData,
    BimContractSeasonData,
    BimContractQuery,
    BimContractConstructionTypeData,
    BimContractSeasonInflationData,
    BimContractConversionIndexData,
    BimContractDefaultsQuery,
    BimContractDefaultData,
    BimTotal_AlcTypeEnum as BimTotalAlcTypeEnum,
    BimContractConstructionTypeResultData,
} from 'gql/graphql';
import { getConstructionTypeListItem, getProjectContractTypeTitle } from 'pages/Project/Common/utils';
import { newDate } from 'utils';

import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { IBimContractResultPlanDataItem } from '../../WorkOrder/types';
import { bimLimitedCostCalcMethods, labelLimitedCost } from '../BimContractEdit/components/types';

import type { TBimContractConversionIndexInput } from './useBimConversionIndicesColumns';
import {
    conversionCoefficientsKeys,
    ConversionCoefficientsKeys,
    getConversionIndicesCoefficientKey,
} from './useBimConversionIndicesColumns';
import type { TBimContractSeasonInflationInputRow } from './useBimSeasonsInflationColumns';
import { getInflationRateKey } from './useBimSeasonsInflationColumns';

const getInflationInputRowBySeasons = (
    seasons?: BimContractSeasonData[] | null,
): TBimContractSeasonInflationInputRow[] =>
    seasons?.map((season, index) => ({ inflationRate: 0, contractSeasonYear: season.year, id: season.year, index })) ||
    [];

const getInflationInputRowBySeasonInflationData = (
    seasonsInflation: BimContractSeasonInflationData,
): TBimContractSeasonInflationInputRow => ({
    inflationRate: seasonsInflation.inflationRate,
    contractSeasonYear: seasonsInflation.contractSeason.year,
    id: seasonsInflation.contractSeason.year,
});

export const getInflation = (initialData: BimContractData) =>
    (
        initialData?.seasonsInflation?.map(getInflationInputRowBySeasonInflationData) ||
        getInflationInputRowBySeasons(initialData?.seasons)
    ).sort((a, b) => a.contractSeasonYear - b.contractSeasonYear);

export const conversionIndexInputToFlat = (item: TBimContractConversionIndexInput) =>
    Object.values(ConversionCoefficientsKeys).reduce((acc: any, key) => {
        acc[getConversionIndicesCoefficientKey(key, item.constructionTypeId)] = item.coefficients[key];

        return acc;
    }, {});

export const conversionIndexDataFlat = (item: BimContractConversionIndexData) =>
    conversionCoefficientsKeys.reduce((acc: any, key) => {
        acc[getConversionIndicesCoefficientKey(key, item.constructionType.id)] = item.coefficients[key];

        return acc;
    }, {});

export const conversionIndexesToFlat = (items?: BimContractConversionIndexData[]) =>
    items?.reduce((acc, item) => ({ ...acc, ...conversionIndexDataFlat(item) }), {}) || {};

export const seasonsInflationToFlat = (seasonsInflation?: BimContractSeasonInflationData[]) =>
    seasonsInflation?.reduce((acc: any, value) => {
        const {
            inflationRate,
            contractSeason: { year },
        } = value;

        acc[getInflationRateKey({ contractSeasonYear: year } as any)] = inflationRate;

        return acc;
    }, {}) || {};

export interface IContractResultCommon {
    contractSeasonYear?: number | null;
    totalType?: BimTotalAlcTypeEnum | null;
    constructionTypePlanResults?: BimContractConstructionTypeResultData[] | null;
}
export const getResultPlanItemId = (item: IContractResultCommon) =>
    item.contractSeasonYear ? `${item.totalType}_${item.contractSeasonYear}` : `${item?.totalType}_${uuidv4()}`;

export const mapResultPlan = (
    items?: (IContractResultCommon | null)[] | null,
): (null | IBimContractResultPlanDataItem)[] =>
    items?.map((item) => {
        if (!item) {
            return item;
        }

        const id = getResultPlanItemId(item);

        const planResults =
            item.constructionTypePlanResults?.reduce(
                (acc, planRes) => {
                    acc[`contractPlan_${planRes.constructionTypeId}`] = planRes.contractPlan;

                    return acc;
                },
                {} as Record<string, any>,
            ) || {};

        return { ...item, id, ...planResults } as IBimContractResultPlanDataItem;
    }) || [];

export const useBimContractFormData = (
    initialData?:
        | BimContractQuery['bimContract']['data']
        | BimContractDefaultsQuery['bimContractDefaults']['data']
        | null,
) =>
    useMemo(() => {
        if ((initialData as BimContractData)?.id) {
            const seasons = (initialData as BimContractData)?.seasons
                ?.map((s) => s.year)
                ?.sort()
                ?.map((year) => new Date(year, 0, 1));

            const limitedCosts = (initialData as BimContractData).limitedCosts.reduce((accLimit: any, limitItem) => {
                const { constructionType, regionalFactor, items } = limitItem;

                const rateValues = items.reduce((acc: any, item) => {
                    acc = {
                        ...acc,
                        [`rateValueLimitedCost_${constructionType.id}_${item.type}`]: item.rateValue,
                        [`fotValueLimitedCost_${constructionType.id}_${item.type}`]: item.fotValue,
                    };

                    return acc;
                }, {});

                const selectedItems = items.map((itemLimit) => ({
                    id: itemLimit.id,
                    label: labelLimitedCost[itemLimit.type],
                    type: itemLimit.type,
                }));

                accLimit = {
                    ...accLimit,
                    ...rateValues,
                    [`regionalFactor_${constructionType.id}`]: regionalFactor,
                    [`typesLimitedCost_${constructionType.id}`]: selectedItems,
                };

                return accLimit;
            }, {});

            const statusFollowers = ((initialData as BimContractData)?.statusFollowers || []).map((v) =>
                getContractStatusListItem(v),
            );

            return {
                ...initialData,
                generalInfo: {
                    ...(initialData as BimContractData).generalInfo,
                    startDate: newDate((initialData as BimContractData)?.generalInfo?.startDate),
                    finishDate: newDate((initialData as BimContractData)?.generalInfo?.finishDate),
                    signingDate: newDate((initialData as BimContractData)?.generalInfo?.signingDate),
                },
                status: getContractStatusListItem((initialData as BimContractData).status),
                statusFollowers,
                contractType: getProjectContractTypeTitle((initialData as BimContractData).contractType),
                constructionTypes: (initialData as BimContractData).constructionTypes?.map((type, index) =>
                    getConstructionTypeListItem(type as BimContractConstructionTypeData, index),
                ),
                usedInObjectStructure:
                    (initialData as BimContractData).constructionTypes
                        ?.filter((c) => c.usedInObjectStructure)
                        .map((c) => c.id) || [],
                seasonsRange: seasons?.length
                    ? [seasons[0], seasons?.length > 1 ? seasons[seasons.length - 1] : seasons[0]]
                    : [undefined, undefined],
                limitedCosts,
                limitedCostCalcMethod: bimLimitedCostCalcMethods.find(
                    (b) => b.id === (initialData as BimContractData).limitedCostCalcMethod,
                ),
                resultPlan: mapResultPlan((initialData as BimContractData)?.resultPlan),
                ...conversionIndexesToFlat((initialData as BimContractData).conversionIndices),
                ...seasonsInflationToFlat((initialData as BimContractData).seasonsInflation),
            };
        }

        const inImplementationStatusItem = { label: 'На реализации', id: ContractStatusEnum.InImplementation };

        return {
            authoredBy: (initialData as BimContractDefaultData)?.authoredBy,
            contractType: getProjectContractTypeTitle((initialData as BimContractDefaultData)?.contractType),
            generalInfo: { customer: (initialData as BimContractDefaultData)?.customer },
            status: getContractStatusListItem((initialData as BimContractDefaultData)?.status),
            statusFollowers: [inImplementationStatusItem],
            resultPlan: mapResultPlan((initialData as BimContractDefaultData)?.resultPlan),
        };
    }, [initialData]);
